import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import SupervisorLayout from '../../components/layout/SupervisorLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  CalendarPlus, Plus, MapPin, Trash2, Clock, Briefcase, Shirt,
  User, Users, CheckCircle, Search, PoundSterling, X, UserPlus,
  Copy, Edit, Bell, Megaphone, Download, Shield,
  ChevronLeft, ChevronRight, ChevronDown, ChevronUp,
  Tent, Home, Car, AlertTriangle, FileText, QrCode
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO, startOfMonth, endOfMonth, eachDayOfInterval, getDay, addMonths, subMonths } from 'date-fns';
import { cn } from '../../lib/utils';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SupervisorUpcomingShiftsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [openShifts, setOpenShifts] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editShift, setEditShift] = useState(null);
  const [manageShift, setManageShift] = useState(null);
  const [manageGroup, setManageGroup] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, shift: null });
  const [duplicateDialog, setDuplicateDialog] = useState({ open: false, shift: null });
  const [creating, setCreating] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [staffSearchTerm, setStaffSearchTerm] = useState('');
  const [duplicateDates, setDuplicateDates] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [duplicateMonth, setDuplicateMonth] = useState(new Date());
  const [expandedGroups, setExpandedGroups] = useState({});
  
  // Batch edit state
  const [batchEditGroup, setBatchEditGroup] = useState(null);
  const [batchEditForm, setBatchEditForm] = useState(null);
  const [batchEditDates, setBatchEditDates] = useState([]);
  const [batchEditOriginalDates, setBatchEditOriginalDates] = useState([]);
  const [batchEditMonth, setBatchEditMonth] = useState(new Date());

  const defaultEvent = {
    location: '', dress_code: '', selectedDates: [],
    start_time: '09:00', end_time: '17:00', position: '',
    quantity: 1, hourly_payment: '', notes: '', sia_required: '',
    restricted_to: [], priority_employees: [],
    camping_provided: null, accommodation_provided: null,
    accommodation_cost_per_day: '', transport_provided: null,
    transport_location: '', transport_departure_time: '',
    transport_notes: '', accommodation_notes: ''
  };

  // Multi-event state
  const [eventForms, setEventForms] = useState([{ ...defaultEvent }]);
  const [activeEventIndex, setActiveEventIndex] = useState(0);
  
  const shiftForm = eventForms[activeEventIndex] || defaultEvent;
  const setShiftForm = (updater) => {
    setEventForms(prev => {
      const copy = [...prev];
      if (typeof updater === 'function') {
        copy[activeEventIndex] = updater(copy[activeEventIndex]);
      } else {
        copy[activeEventIndex] = updater;
      }
      return copy;
    });
  };

  const fetchOpenShifts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/open-shifts`);
      setOpenShifts(response.data);
    } catch (error) {
      console.error('Failed to fetch open shifts:', error);
      toast.error('Failed to load upcoming shifts');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAllUsers = useCallback(async () => {
    try {
      const [empRes, supRes] = await Promise.all([
        axios.get(`${API}/supervisor/employees`),
        axios.get(`${API}/supervisor/supervisors`)
      ]);
      setAllUsers([...empRes.data, ...supRes.data]);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  }, []);

  useEffect(() => {
    fetchOpenShifts();
    fetchAllUsers();
  }, [fetchOpenShifts, fetchAllUsers]);

  const handleDateToggle = (dateStr) => {
    setShiftForm(prev => {
      const dates = prev.selectedDates.includes(dateStr)
        ? prev.selectedDates.filter(d => d !== dateStr)
        : [...prev.selectedDates, dateStr];
      return { ...prev, selectedDates: dates };
    });
  };

  const getMonthDays = (date) => {
    const firstDay = startOfMonth(date);
    const lastDay = endOfMonth(date);
    const days = eachDayOfInterval({ start: firstDay, end: lastDay });
    const startPadding = getDay(firstDay);
    return {
      monthLabel: format(date, 'MMMM yyyy'),
      days: days.map(d => ({
        date: d, dateStr: format(d, 'yyyy-MM-dd'),
        dayNum: format(d, 'd'),
        isToday: format(d, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd')
      })),
      startPadding
    };
  };

  const handleCreateShift = async (e) => {
    e.preventDefault();
    for (let i = 0; i < eventForms.length; i++) {
      if (eventForms[i].selectedDates.length === 0) {
        toast.error(`Event ${i + 1}: Please select at least one date`);
        setActiveEventIndex(i);
        return;
      }
    }
    setCreating(true);
    try {
      const events = eventForms.map(form => ({
        location: form.location || '', dress_code: form.dress_code || '',
        dates: form.selectedDates.sort(),
        start_time: form.start_time || '09:00', end_time: form.end_time || '17:00',
        position: form.position || '', quantity: parseInt(form.quantity) || 1,
        hourly_payment: form.hourly_payment ? parseFloat(form.hourly_payment) : null,
        notes: form.notes || '', sia_required: form.sia_required || null,
        restricted_to: form.restricted_to.length > 0 ? form.restricted_to : null,
        priority_employees: form.priority_employees.length > 0 ? form.priority_employees : null,
        camping_provided: form.camping_provided,
        accommodation_provided: form.accommodation_provided,
        accommodation_cost_per_day: form.accommodation_provided && form.accommodation_cost_per_day ? parseFloat(form.accommodation_cost_per_day) : null,
        transport_provided: form.transport_provided,
        transport_location: form.transport_provided ? form.transport_location : null,
        transport_departure_time: form.transport_provided ? form.transport_departure_time : null,
        transport_notes: form.transport_notes || null,
        accommodation_notes: form.accommodation_notes || null
      }));
      const response = await axios.post(`${API}/open-shifts/batch`, { events });
      toast.success(response.data.message);
      setCreateOpen(false);
      resetForm();
      fetchOpenShifts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create shifts');
    } finally {
      setCreating(false);
    }
  };

  const handleUpdateShift = async (e) => {
    e.preventDefault();
    if (!editShift) return;
    setCreating(true);
    try {
      const payload = {
        location: shiftForm.location || null, dress_code: shiftForm.dress_code || null,
        date: shiftForm.selectedDates[0] || null,
        start_time: shiftForm.start_time || null, end_time: shiftForm.end_time || null,
        position: shiftForm.position || null,
        quantity: shiftForm.quantity ? parseInt(shiftForm.quantity) : null,
        hourly_payment: shiftForm.hourly_payment ? parseFloat(shiftForm.hourly_payment) : null,
        notes: shiftForm.notes || null, sia_required: shiftForm.sia_required || null,
        camping_provided: shiftForm.camping_provided,
        accommodation_provided: shiftForm.accommodation_provided,
        accommodation_cost_per_day: shiftForm.accommodation_provided && shiftForm.accommodation_cost_per_day ? parseFloat(shiftForm.accommodation_cost_per_day) : null,
        transport_provided: shiftForm.transport_provided,
        transport_location: shiftForm.transport_provided ? shiftForm.transport_location : null,
        transport_departure_time: shiftForm.transport_provided ? shiftForm.transport_departure_time : null,
        transport_notes: shiftForm.transport_notes || null,
        accommodation_notes: shiftForm.accommodation_notes || null
      };
      await axios.put(`${API}/open-shifts/${editShift.id}`, payload);
      toast.success('Shift updated successfully');
      setEditShift(null);
      resetForm();
      fetchOpenShifts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update shift');
    } finally {
      setCreating(false);
    }
  };

  const resetForm = () => {
    setEventForms([{ ...defaultEvent }]);
    setActiveEventIndex(0);
    setCurrentMonth(new Date());
  };

  const openEditDialog = (shift) => {
    setEventForms([{
      location: shift.location || '', dress_code: shift.dress_code || '',
      selectedDates: [shift.date],
      start_time: shift.start_time || '09:00', end_time: shift.end_time || '17:00',
      position: shift.position || '', quantity: shift.quantity || 1,
      hourly_payment: shift.hourly_payment || '', notes: shift.notes || '',
      sia_required: shift.sia_required || '',
      restricted_to: shift.restricted_to || [], priority_employees: shift.priority_employees || [],
      camping_provided: shift.camping_provided,
      accommodation_provided: shift.accommodation_provided,
      accommodation_cost_per_day: shift.accommodation_cost_per_day || '',
      transport_provided: shift.transport_provided,
      transport_location: shift.transport_location || '',
      transport_departure_time: shift.transport_departure_time || '',
      transport_notes: shift.transport_notes || '',
      accommodation_notes: shift.accommodation_notes || ''
    }]);
    setActiveEventIndex(0);
    setCurrentMonth(parseISO(shift.date));
    setEditShift(shift);
  };

  const addEvent = () => {
    setEventForms(prev => [...prev, { ...defaultEvent }]);
    setActiveEventIndex(eventForms.length);
  };

  const removeEvent = (index) => {
    if (eventForms.length <= 1) return;
    setEventForms(prev => prev.filter((_, i) => i !== index));
    setActiveEventIndex(prev => Math.min(prev, eventForms.length - 2));
  };

  const handleDeleteShift = async () => {
    if (!deleteDialog.shift) return;
    try {
      await axios.delete(`${API}/open-shifts/${deleteDialog.shift.id}`);
      toast.success('Shift deleted successfully');
      setDeleteDialog({ open: false, shift: null });
      fetchOpenShifts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete shift');
    }
  };

  const handleDuplicateShift = async () => {
    if (!duplicateDialog.shift || duplicateDates.length === 0) return;
    try {
      const response = await axios.post(`${API}/open-shifts/${duplicateDialog.shift.id}/duplicate`, duplicateDates);
      toast.success(response.data.message);
      setDuplicateDialog({ open: false, shift: null });
      setDuplicateDates([]);
      fetchOpenShifts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to duplicate shift');
    }
  };

  const handleRemoveStaff = async (shiftId, userId) => {
    try {
      const res = await axios.post(`${API}/open-shifts/${shiftId}/remove-staff?user_id=${userId}`);
      toast.success(res.data.message || 'Staff removed');
      fetchOpenShifts();
      const updated = await axios.get(`${API}/open-shifts/${shiftId}`);
      setManageShift(updated.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove staff');
    }
  };

  const handleAddStaff = async (shiftId, userId) => {
    try {
      if (manageGroup && manageGroup.shifts.length > 1) {
        const allShiftIds = manageGroup.shifts.map(s => s.id);
        await axios.post(`${API}/open-shifts/bulk-add-staff`, { user_id: userId, shift_ids: allShiftIds });
        toast.success('Staff added to all event days');
      } else {
        await axios.post(`${API}/open-shifts/${shiftId}/add-staff`, { user_id: userId });
        toast.success('Staff added to shift');
      }
      fetchOpenShifts();
      const updated = await axios.get(`${API}/open-shifts/${shiftId}`);
      setManageShift(updated.data);
      setStaffSearchTerm('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add staff');
    }
  };

  const handleSendReminder = async (shiftId) => {
    try {
      const response = await axios.post(`${API}/open-shifts/${shiftId}/send-reminder`);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reminder');
    }
  };

  const handleBroadcast = async (shiftId) => {
    try {
      const response = await axios.post(`${API}/open-shifts/${shiftId}/broadcast`);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to broadcast');
    }
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API}/open-shifts/export`);
      const data = response.data.shifts;
      const headers = ['Date', 'Position', 'Location', 'Start Time', 'End Time', 'Staff Needed', 'Staff Assigned', 'Hourly Rate', 'Status', 'Camping', 'Accommodation', 'Transport'];
      const csvRows = [headers.join(',')];
      data.forEach(row => {
        csvRows.push([row.date, `"${row.position}"`, `"${row.location}"`, row.start_time, row.end_time, row.staff_needed, row.staff_assigned, row.hourly_rate || '', row.status, row.camping_provided ? 'Yes' : 'No', row.accommodation_provided ? 'Yes' : 'No', row.transport_provided ? 'Yes' : 'No'].join(','));
      });
      const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `shifts_export_${format(new Date(), 'yyyy-MM-dd')}.csv`;
      a.click();
      toast.success(`Exported ${data.length} shifts`);
    } catch (error) {
      toast.error('Failed to export shifts');
    }
  };

  const formatDate = (dateStr) => {
    try { return format(parseISO(dateStr), 'EEE, MMM d, yyyy'); } catch { return dateStr; }
  };

  const handleGenerateBadge = async (group) => {
    try {
      const response = await axios.post(`${API}/feedback/generate-badge`, {
        position: group.position,
        location: group.location,
        start_time: group.start_time,
        end_time: group.end_time
      });
      const data = response.data;
      const params = new URLSearchParams({
        code: data.code,
        location: data.location,
        dates: data.dates.join(','),
        start_time: data.start_time,
        end_time: data.end_time
      });
      navigate(`/print-badge?${params.toString()}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate badge');
    }
  };

  // Open batch edit dialog for entire grouped event
  const openBatchEditDialog = (group) => {
    const dates = [...new Set(group.shifts.map(s => s.date))].sort();
    setBatchEditGroup(group);
    setBatchEditDates([...dates]);
    setBatchEditOriginalDates([...dates]);
    setBatchEditMonth(dates.length > 0 ? parseISO(dates[0]) : new Date());
    setBatchEditForm({
      location: group.location || '', dress_code: group.dress_code || '',
      start_time: group.start_time || '09:00', end_time: group.end_time || '17:00',
      position: group.position || '', quantity: group.shifts[0]?.quantity || 1,
      hourly_payment: group.hourly_payment || '', notes: group.notes || '',
      sia_required: group.sia_required || '',
      camping_provided: group.camping_provided,
      accommodation_provided: group.accommodation_provided,
      accommodation_cost_per_day: group.accommodation_cost_per_day || '',
      transport_provided: group.transport_provided,
      transport_location: group.transport_location || '',
      transport_departure_time: group.transport_departure_time || '',
      transport_notes: group.transport_notes || '',
      accommodation_notes: group.accommodation_notes || ''
    });
  };

  const handleBatchUpdate = async (e) => {
    e.preventDefault();
    if (!batchEditGroup || !batchEditForm) return;
    if (batchEditDates.length === 0) {
      toast.error('Event must have at least one date');
      return;
    }
    setCreating(true);
    try {
      const addedDates = batchEditDates.filter(d => !batchEditOriginalDates.includes(d));
      const removedDates = batchEditOriginalDates.filter(d => !batchEditDates.includes(d));
      const remainingDates = batchEditDates.filter(d => batchEditOriginalDates.includes(d));

      for (const dateStr of removedDates) {
        const shiftsForDate = batchEditGroup.shifts.filter(s => s.date === dateStr);
        for (const shift of shiftsForDate) {
          try { await axios.delete(`${API}/open-shifts/${shift.id}?force=true`); } catch (err) { console.warn(`Could not delete shift for ${dateStr}`); }
        }
      }

      if (addedDates.length > 0) {
        await axios.post(`${API}/open-shifts`, {
          location: batchEditForm.location || '', dress_code: batchEditForm.dress_code || '',
          dates: addedDates,
          start_time: batchEditForm.start_time || '09:00', end_time: batchEditForm.end_time || '17:00',
          position: batchEditForm.position || '',
          quantity: parseInt(batchEditForm.quantity) || 1,
          hourly_payment: batchEditForm.hourly_payment ? parseFloat(batchEditForm.hourly_payment) : null,
          notes: batchEditForm.notes || '', sia_required: batchEditForm.sia_required || null,
          camping_provided: batchEditForm.camping_provided,
          accommodation_provided: batchEditForm.accommodation_provided,
          accommodation_cost_per_day: batchEditForm.accommodation_provided && batchEditForm.accommodation_cost_per_day ? parseFloat(batchEditForm.accommodation_cost_per_day) : null,
          transport_provided: batchEditForm.transport_provided,
          transport_location: batchEditForm.transport_provided ? batchEditForm.transport_location : null,
          transport_departure_time: batchEditForm.transport_provided ? batchEditForm.transport_departure_time : null,
          transport_notes: batchEditForm.transport_notes || null,
          accommodation_notes: batchEditForm.accommodation_notes || null
        });
      }

      if (remainingDates.length > 0) {
        const shiftIdsToUpdate = batchEditGroup.shifts
          .filter(s => remainingDates.includes(s.date))
          .map(s => s.id);
        
        await axios.put(`${API}/open-shifts/batch-update-by-ids`, {
          shift_ids: shiftIdsToUpdate,
          update_data: {
            location: batchEditForm.location || null, dress_code: batchEditForm.dress_code || null,
            start_time: batchEditForm.start_time || null, end_time: batchEditForm.end_time || null,
            position: batchEditForm.position || null,
            quantity: batchEditForm.quantity ? parseInt(batchEditForm.quantity) : null,
            hourly_payment: batchEditForm.hourly_payment ? parseFloat(batchEditForm.hourly_payment) : null,
            notes: batchEditForm.notes || null, sia_required: batchEditForm.sia_required || null,
            camping_provided: batchEditForm.camping_provided,
            accommodation_provided: batchEditForm.accommodation_provided,
            accommodation_cost_per_day: batchEditForm.accommodation_provided && batchEditForm.accommodation_cost_per_day ? parseFloat(batchEditForm.accommodation_cost_per_day) : null,
            transport_provided: batchEditForm.transport_provided,
            transport_location: batchEditForm.transport_provided ? batchEditForm.transport_location : null,
            transport_departure_time: batchEditForm.transport_provided ? batchEditForm.transport_departure_time : null,
            transport_notes: batchEditForm.transport_notes || null,
            accommodation_notes: batchEditForm.accommodation_notes || null
          }
        });
      }

      const parts = [];
      if (remainingDates.length > 0) parts.push(`Updated ${remainingDates.length} days`);
      if (addedDates.length > 0) parts.push(`Added ${addedDates.length} new days`);
      if (removedDates.length > 0) parts.push(`Removed ${removedDates.length} days`);
      toast.success(parts.join(', ') || 'Event updated');

      setBatchEditGroup(null); setBatchEditForm(null);
      setBatchEditDates([]); setBatchEditOriginalDates([]);
      fetchOpenShifts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to batch update');
    } finally {
      setCreating(false);
    }
  };

  const filteredShifts = openShifts.filter(shift => {
    const matchesSearch = shift.location?.toLowerCase().includes(searchTerm.toLowerCase()) || shift.position?.toLowerCase().includes(searchTerm.toLowerCase());
    if (filterStatus === 'available') return matchesSearch && (shift.claimed_count || 0) < (shift.quantity || 1);
    if (filterStatus === 'full') return matchesSearch && (shift.claimed_count || 0) >= (shift.quantity || 1);
    return matchesSearch;
  });

  const availableShifts = filteredShifts.filter(s => (s.claimed_count || 0) < (s.quantity || 1));
  const fullShifts = filteredShifts.filter(s => (s.claimed_count || 0) >= (s.quantity || 1));
  const filteredUsers = allUsers.filter(u => !staffSearchTerm || u.name?.toLowerCase().includes(staffSearchTerm.toLowerCase()));

  // Group shifts by position+location+time into events
  const groupShifts = (shifts) => {
    const groups = {};
    shifts.forEach(shift => {
      const key = `${shift.position || ''}|${shift.location || ''}|${shift.start_time || ''}|${shift.end_time || ''}`;
      if (!groups[key]) {
        groups[key] = {
          key, position: shift.position || 'TBD', location: shift.location || 'TBD',
          start_time: shift.start_time || 'TBD', end_time: shift.end_time || 'TBD',
          dress_code: shift.dress_code || '', hourly_payment: shift.hourly_payment,
          sia_required: shift.sia_required, notes: shift.notes,
          camping_provided: shift.camping_provided,
          accommodation_provided: shift.accommodation_provided,
          accommodation_cost_per_day: shift.accommodation_cost_per_day,
          transport_provided: shift.transport_provided,
          transport_location: shift.transport_location,
          transport_departure_time: shift.transport_departure_time,
          transport_notes: shift.transport_notes,
          accommodation_notes: shift.accommodation_notes,
          shifts: []
        };
      }
      groups[key].shifts.push(shift);
    });
    Object.values(groups).forEach(g => g.shifts.sort((a, b) => (a.date || '').localeCompare(b.date || '')));
    return Object.values(groups);
  };

  const toggleGroup = (key) => setExpandedGroups(prev => ({ ...prev, [key]: !prev[key] }));

  const getGroupEmployees = (group) => {
    const map = {};
    group.shifts.forEach(shift => {
      (shift.claimed_by_list || []).forEach(c => { if (!map[c.user_id]) map[c.user_id] = { ...c }; });
    });
    return Object.values(map);
  };

  const getDateRange = (group) => {
    const dates = [...new Set((group.shifts || []).map(s => s.date).filter(Boolean))].sort();
    if (dates.length === 0) return '';
    if (dates.length === 1) return formatDate(dates[0]);
    try {
      return `${format(parseISO(dates[0]), 'MMM d')} - ${format(parseISO(dates[dates.length - 1]), 'MMM d, yyyy')}`;
    } catch {
      return `${dates[0]} - ${dates[dates.length - 1]}`;
    }
  };

  const renderGroupCard = (group, isFull = false) => {
    const isExpanded = expandedGroups[group.key];
    const employees = getGroupEmployees(group);
    const dayCount = new Set(group.shifts.map(s => s.date)).size;
    return (
      <Card key={group.key} className={cn("bg-card border-border rounded-sm transition-colors", isFull ? "border-green-500/30" : "hover:border-blue-500/50")} data-testid={`event-group-${group.key}`}>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-start justify-between">
            <Badge className={cn("border", isFull ? "bg-green-500/20 text-green-400 border-green-500/30" : "bg-blue-500/20 text-blue-400 border-blue-500/30")}>
              {employees.length} staff | {dayCount} day{dayCount !== 1 ? 's' : ''}
            </Badge>
            <div className="flex gap-1">
              {group.shifts[0] && (
                <Button variant="ghost" size="icon" onClick={() => openBatchEditDialog(group)} className="h-7 w-7 text-muted-foreground hover:text-primary" title="Edit Event" data-testid={`edit-group-${group.key}`}>
                  <Edit className="w-4 h-4" />
                </Button>
              )}
              {group.shifts[0] && <Button variant="ghost" size="icon" onClick={() => setDuplicateDialog({ open: true, shift: group.shifts[0] })} className="h-7 w-7 text-muted-foreground hover:text-blue-400"><Copy className="w-4 h-4" /></Button>}
              {group.shifts[0] && <Button variant="ghost" size="icon" onClick={() => setDeleteDialog({ open: true, shift: group.shifts[0] })} className="h-7 w-7 text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></Button>}
            </div>
          </div>
          <div className="space-y-2">
            <p className="text-lg font-medium flex items-center gap-2"><Briefcase className="w-4 h-4 text-primary" />{group.position}</p>
            <p className="text-sm text-muted-foreground flex items-center gap-2"><MapPin className="w-4 h-4" />{group.location}</p>
            <p className="text-sm text-muted-foreground flex items-center gap-2"><Clock className="w-4 h-4" />{getDateRange(group)} | {group.start_time} - {group.end_time}</p>
            {group.dress_code && <p className="text-sm text-muted-foreground flex items-center gap-2"><Shirt className="w-4 h-4" />{group.dress_code}</p>}
            {group.hourly_payment && <p className="text-sm font-medium text-green-400 flex items-center gap-2"><PoundSterling className="w-4 h-4" />&pound;{group.hourly_payment.toFixed(2)}/hour</p>}
            {group.sia_required && group.sia_required !== 'none' && <p className="text-sm text-yellow-400 flex items-center gap-2"><Shield className="w-4 h-4" />SIA: {group.sia_required === 'door_supervisor' ? 'Door Supervisor' : 'CCTV'}</p>}
          </div>
          <button onClick={() => toggleGroup(group.key)} className="w-full flex items-center justify-between p-2 bg-accent/50 rounded-sm hover:bg-accent transition-colors" data-testid={`toggle-group-${group.key}`}>
            <span className="text-sm text-muted-foreground flex items-center gap-2"><Users className="w-4 h-4" />{employees.length} employee{employees.length !== 1 ? 's' : ''} assigned</span>
            {isExpanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
          </button>
          {isExpanded && (
            <div className="space-y-1 pl-2 border-l-2 border-primary/30">
              {employees.length > 0 ? employees.map((emp) => (
                <div key={emp.user_id} className="flex items-center gap-2 py-1">
                  <User className="w-3.5 h-3.5 text-muted-foreground" />
                  <span className="text-sm text-foreground">{emp.user_name}</span>
                  <Badge variant="outline" className="text-xs">{emp.user_role}</Badge>
                </div>
              )) : <p className="text-xs text-muted-foreground py-1">No staff assigned yet</p>}
            </div>
          )}
          <div className="flex gap-2 pt-2 border-t border-border">
            {group.shifts[0] && <Button variant="outline" onClick={() => { setManageShift(group.shifts[0]); setManageGroup(group); }} className="flex-1 border-border rounded-sm"><Users className="w-4 h-4 mr-2" />Manage</Button>}
            <Button variant="outline" onClick={() => handleGenerateBadge(group)} className="flex-1 border-border rounded-sm" data-testid={`qr-badge-${group.key}`}><QrCode className="w-4 h-4 mr-2" />QR Badge</Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return <SupervisorLayout><div className="p-6 flex items-center justify-center min-h-screen"><div className="animate-pulse text-muted-foreground">Loading...</div></div></SupervisorLayout>;
  }

  const monthData = getMonthDays(currentMonth);
  const duplicateMonthData = getMonthDays(duplicateMonth);

  return (
    <SupervisorLayout>
      <div className="p-4 sm:p-6 space-y-6 min-h-screen">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-border pb-6">
          <div>
            <h1 className="text-2xl font-barlow tracking-widest flex items-center gap-3"><CalendarPlus className="w-6 h-6 text-primary" />UPCOMING SHIFTS</h1>
            <p className="text-sm text-muted-foreground mt-1">Create and manage shifts for staff to claim</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleExport} className="border-border rounded-sm"><Download className="w-4 h-4 mr-2" />Export</Button>
            <Button onClick={() => setCreateOpen(true)} data-testid="create-open-shift-btn" className="bg-primary text-primary-foreground hover:bg-yellow-300 rounded-sm uppercase tracking-wider"><Plus className="w-4 h-4 mr-2" />Add Shifts</Button>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder="Search by location or position..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 bg-background border-border rounded-sm" />
          </div>
          <div className="flex gap-2">
            <Button variant={filterStatus === 'all' ? 'default' : 'outline'} onClick={() => setFilterStatus('all')} className={cn("rounded-sm", filterStatus === 'all' && "bg-primary text-black")}>All ({openShifts.length})</Button>
            <Button variant={filterStatus === 'available' ? 'default' : 'outline'} onClick={() => setFilterStatus('available')} className={cn("rounded-sm", filterStatus === 'available' && "bg-blue-500 text-foreground")}>Available ({availableShifts.length})</Button>
            <Button variant={filterStatus === 'full' ? 'default' : 'outline'} onClick={() => setFilterStatus('full')} className={cn("rounded-sm", filterStatus === 'full' && "bg-green-500 text-foreground")}>Full ({fullShifts.length})</Button>
          </div>
        </div>

        {(filterStatus === 'all' || filterStatus === 'available') && availableShifts.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-sm font-barlow tracking-wider uppercase text-blue-400 flex items-center gap-2"><Clock className="w-4 h-4" />Available Events ({groupShifts(availableShifts).length})</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-[60vh] overflow-y-auto pr-2">
              {groupShifts(availableShifts).map((group) => renderGroupCard(group, false))}
            </div>
          </div>
        )}

        {(filterStatus === 'all' || filterStatus === 'full') && fullShifts.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-sm font-barlow tracking-wider uppercase text-green-400 flex items-center gap-2"><CheckCircle className="w-4 h-4" />Full Events ({groupShifts(fullShifts).length})</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-[60vh] overflow-y-auto pr-2">
              {groupShifts(fullShifts).map((group) => renderGroupCard(group, true))}
            </div>
          </div>
        )}

        {filteredShifts.length === 0 && (
          <Card className="bg-card border-border rounded-sm">
            <CardContent className="p-12 text-center">
              <CalendarPlus className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-medium mb-2">No Shifts Found</h3>
              <p className="text-sm text-muted-foreground mb-4">{searchTerm || filterStatus !== 'all' ? 'Try adjusting filters' : 'Create shifts for staff to claim'}</p>
            </CardContent>
          </Card>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={createOpen || !!editShift} onOpenChange={(open) => { if (!open) { setCreateOpen(false); setEditShift(null); resetForm(); } }}>
          <DialogContent className="bg-card border-border rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-barlow tracking-wider uppercase">{editShift ? 'Edit Shift' : 'Create Open Shifts'}</DialogTitle></DialogHeader>

            {/* Multi-event tabs (only in create mode) */}
            {!editShift && eventForms.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap mt-2 pb-2 border-b border-border">
                {eventForms.map((ev, idx) => (
                  <div key={idx} className="flex items-center">
                    <button type="button" onClick={() => setActiveEventIndex(idx)}
                      className={cn("px-3 py-1.5 rounded-sm text-xs font-medium transition-colors", activeEventIndex === idx ? "bg-primary text-primary-foreground" : "bg-accent text-muted-foreground hover:text-foreground")}
                      data-testid={`event-tab-${idx}`}>
                      Event {idx + 1}{ev.position ? `: ${ev.position.substring(0, 15)}` : ''}{ev.selectedDates.length > 0 ? ` (${ev.selectedDates.length}d)` : ''}
                    </button>
                    {eventForms.length > 1 && (
                      <button type="button" onClick={() => removeEvent(idx)} className="ml-1 p-0.5 text-muted-foreground hover:text-destructive transition-colors" data-testid={`remove-event-${idx}`}>
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                ))}
                <button type="button" onClick={addEvent} className="px-3 py-1.5 rounded-sm text-xs font-medium border border-dashed border-primary text-primary hover:bg-primary/10 transition-colors flex items-center gap-1" data-testid="add-event-btn">
                  <Plus className="w-3 h-3" /> Add Event
                </button>
              </div>
            )}

            <form onSubmit={editShift ? handleUpdateShift : handleCreateShift} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-2"><Briefcase className="w-4 h-4" />Position</Label>
                  <Input value={shiftForm.position} onChange={(e) => setShiftForm({ ...shiftForm, position: e.target.value })} placeholder="e.g., Security Guard" className="bg-background border-border rounded-sm" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-2"><MapPin className="w-4 h-4" />Location</Label>
                  <Input value={shiftForm.location} onChange={(e) => setShiftForm({ ...shiftForm, location: e.target.value })} placeholder="e.g., Main Office" className="bg-background border-border rounded-sm" />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-2"><Shirt className="w-4 h-4" />Dress Code</Label>
                <Input value={shiftForm.dress_code} onChange={(e) => setShiftForm({ ...shiftForm, dress_code: e.target.value })} placeholder="e.g., Full Black Suit" className="bg-background border-border rounded-sm" />
              </div>

              {/* Calendar */}
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">{editShift ? 'Date' : `Select Dates (${shiftForm.selectedDates.length} selected)`}</Label>
                <div className="bg-muted border border-border rounded-sm p-4">
                  <div className="border border-border rounded-sm p-3">
                    <div className="flex items-center justify-between mb-3">
                      <Button type="button" variant="ghost" size="icon" onClick={() => setCurrentMonth(m => subMonths(m, 1))} className="h-10 w-10"><ChevronLeft className="w-5 h-5" /></Button>
                      <span className="text-lg font-medium">{monthData.monthLabel}</span>
                      <Button type="button" variant="ghost" size="icon" onClick={() => setCurrentMonth(m => addMonths(m, 1))} className="h-10 w-10"><ChevronRight className="w-5 h-5" /></Button>
                    </div>
                    <div className="grid grid-cols-7 mb-2">{['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((d, i) => <div key={i} className="text-center text-sm text-muted-foreground py-2">{d}</div>)}</div>
                    <div className="grid grid-cols-7 gap-1">
                      {Array.from({ length: monthData.startPadding }).map((_, i) => <div key={`pad-${i}`} className="h-10" />)}
                      {monthData.days.map((day) => (
                        <button key={day.dateStr} type="button" onClick={() => { if (editShift) setShiftForm({ ...shiftForm, selectedDates: [day.dateStr] }); else handleDateToggle(day.dateStr); }}
                          className={cn("h-10 w-full text-lg rounded-sm flex items-center justify-center transition-all font-medium", shiftForm.selectedDates.includes(day.dateStr) ? "bg-primary text-black" : day.isToday ? "bg-blue-500/30 text-blue-300 border border-blue-500/50" : "hover:bg-accent text-foreground")}>
                          {day.dayNum}
                        </button>
                      ))}
                    </div>
                  </div>
                  {shiftForm.selectedDates.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-border">
                      <p className="text-sm text-muted-foreground uppercase tracking-wider">
                        {shiftForm.selectedDates.length === 1 ? format(parseISO(shiftForm.selectedDates[0]), 'EEEE, MMM d') : `${shiftForm.selectedDates.length} dates selected`}
                      </p>
                      {shiftForm.selectedDates.length > 1 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {shiftForm.selectedDates.sort().slice(0, 10).map(d => (
                            <Badge key={d} variant="outline" className="text-xs bg-primary/20 text-primary border-primary/50">{format(parseISO(d), 'MMM d')}</Badge>
                          ))}
                          {shiftForm.selectedDates.length > 10 && <Badge variant="outline" className="text-xs">+{shiftForm.selectedDates.length - 10} more</Badge>}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {!editShift && (
                  <Button type="button" variant="outline" size="sm" onClick={() => setShiftForm({ ...shiftForm, selectedDates: [] })} className="text-xs border-border">Clear All</Button>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label className="text-xs uppercase tracking-wider text-muted-foreground">Start Time</Label><Input type="time" value={shiftForm.start_time} onChange={(e) => setShiftForm({ ...shiftForm, start_time: e.target.value })} className="bg-background border-border rounded-sm" /></div>
                <div className="space-y-2"><Label className="text-xs uppercase tracking-wider text-muted-foreground">End Time</Label><Input type="time" value={shiftForm.end_time} onChange={(e) => setShiftForm({ ...shiftForm, end_time: e.target.value })} className="bg-background border-border rounded-sm" /></div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2"><Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-2"><Users className="w-4 h-4" />Staff Needed</Label><Input type="number" min="1" value={shiftForm.quantity} onChange={(e) => setShiftForm({ ...shiftForm, quantity: e.target.value })} className="bg-background border-border rounded-sm" /></div>
                <div className="space-y-2"><Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-2"><PoundSterling className="w-4 h-4" />Hourly Rate</Label><Input type="number" step="0.01" value={shiftForm.hourly_payment} onChange={(e) => setShiftForm({ ...shiftForm, hourly_payment: e.target.value })} placeholder="12.50" className="bg-background border-border rounded-sm" /></div>
                <div className="space-y-2"><Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-2"><Shield className="w-4 h-4" />SIA Required</Label>
                  <Select value={shiftForm.sia_required} onValueChange={(val) => setShiftForm({ ...shiftForm, sia_required: val })}><SelectTrigger className="bg-background border-border rounded-sm"><SelectValue placeholder="None" /></SelectTrigger><SelectContent><SelectItem value="none">None</SelectItem><SelectItem value="door_supervisor">Door Supervisor</SelectItem><SelectItem value="cctv">CCTV</SelectItem></SelectContent></Select>
                </div>
              </div>

              {/* Camping */}
              <div className="p-4 bg-muted border border-border rounded-sm">
                <div className="flex items-center justify-between">
                  <Label className="text-sm uppercase tracking-wider text-muted-foreground flex items-center gap-2"><Tent className="w-4 h-4 text-green-400" /> Camping Provided</Label>
                  <div className="flex gap-2">
                    <Button type="button" size="sm" variant={shiftForm.camping_provided === true ? 'default' : 'outline'} onClick={() => setShiftForm({ ...shiftForm, camping_provided: true })} className={cn("rounded-sm", shiftForm.camping_provided === true && "bg-green-600")}>Yes</Button>
                    <Button type="button" size="sm" variant={shiftForm.camping_provided === false ? 'default' : 'outline'} onClick={() => setShiftForm({ ...shiftForm, camping_provided: false })} className={cn("rounded-sm", shiftForm.camping_provided === false && "bg-secondary")}>No</Button>
                  </div>
                </div>
              </div>

              {/* Accommodation */}
              <div className="p-4 bg-muted border border-border rounded-sm space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm uppercase tracking-wider text-muted-foreground flex items-center gap-2"><Home className="w-4 h-4 text-blue-400" /> Accommodation Provided</Label>
                  <div className="flex gap-2">
                    <Button type="button" size="sm" variant={shiftForm.accommodation_provided === true ? 'default' : 'outline'} onClick={() => setShiftForm({ ...shiftForm, accommodation_provided: true })} className={cn("rounded-sm", shiftForm.accommodation_provided === true && "bg-green-600")}>Yes</Button>
                    <Button type="button" size="sm" variant={shiftForm.accommodation_provided === false ? 'default' : 'outline'} onClick={() => setShiftForm({ ...shiftForm, accommodation_provided: false })} className={cn("rounded-sm", shiftForm.accommodation_provided === false && "bg-secondary")}>No</Button>
                  </div>
                </div>
                {shiftForm.accommodation_provided === true && (
                  <div className="space-y-2 pt-2 border-t border-border">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Cost Per Staff Per Day</Label>
                    <Input type="number" step="0.01" value={shiftForm.accommodation_cost_per_day} onChange={(e) => setShiftForm({ ...shiftForm, accommodation_cost_per_day: e.target.value })} placeholder="25.00" className="bg-background border-border rounded-sm" />
                  </div>
                )}
                {shiftForm.accommodation_provided === false && <p className="text-sm text-amber-400 flex items-center gap-2 pt-2 border-t border-border"><AlertTriangle className="w-4 h-4" />Staff will see: "You have to sort your own accommodation"</p>}
                <div className="space-y-2 pt-2 border-t border-border">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Accommodation Notes</Label>
                  <Textarea value={shiftForm.accommodation_notes} onChange={(e) => setShiftForm({ ...shiftForm, accommodation_notes: e.target.value })} placeholder="Any notes about accommodation..." className="bg-background border-border rounded-sm resize-none" rows={2} data-testid="accommodation-notes-input" />
                </div>
              </div>

              {/* Transport */}
              <div className="p-4 bg-muted border border-border rounded-sm space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm uppercase tracking-wider text-muted-foreground flex items-center gap-2"><Car className="w-4 h-4 text-purple-400" /> Transport Provided</Label>
                  <div className="flex gap-2">
                    <Button type="button" size="sm" variant={shiftForm.transport_provided === true ? 'default' : 'outline'} onClick={() => setShiftForm({ ...shiftForm, transport_provided: true })} className={cn("rounded-sm", shiftForm.transport_provided === true && "bg-green-600")}>Yes</Button>
                    <Button type="button" size="sm" variant={shiftForm.transport_provided === false ? 'default' : 'outline'} onClick={() => setShiftForm({ ...shiftForm, transport_provided: false })} className={cn("rounded-sm", shiftForm.transport_provided === false && "bg-secondary")}>No</Button>
                  </div>
                </div>
                {shiftForm.transport_provided === true && (
                  <div className="grid grid-cols-2 gap-4 pt-2 border-t border-border">
                    <div className="space-y-2"><Label className="text-xs uppercase tracking-wider text-muted-foreground">Pickup Location</Label><Input value={shiftForm.transport_location} onChange={(e) => setShiftForm({ ...shiftForm, transport_location: e.target.value })} placeholder="London Office" className="bg-background border-border rounded-sm" /></div>
                    <div className="space-y-2"><Label className="text-xs uppercase tracking-wider text-muted-foreground">Departure Time</Label><Input type="time" value={shiftForm.transport_departure_time} onChange={(e) => setShiftForm({ ...shiftForm, transport_departure_time: e.target.value })} className="bg-background border-border rounded-sm" /></div>
                  </div>
                )}
                {shiftForm.transport_provided === false && <p className="text-sm text-amber-400 flex items-center gap-2 pt-2 border-t border-border"><AlertTriangle className="w-4 h-4" />Staff will see: "You have to make your own way"</p>}
                <div className="space-y-2 pt-2 border-t border-border">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Transport Notes</Label>
                  <Textarea value={shiftForm.transport_notes} onChange={(e) => setShiftForm({ ...shiftForm, transport_notes: e.target.value })} placeholder="Any notes about transport..." className="bg-background border-border rounded-sm resize-none" rows={2} data-testid="transport-notes-input" />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-2"><FileText className="w-4 h-4" />Notes</Label>
                <Textarea value={shiftForm.notes} onChange={(e) => setShiftForm({ ...shiftForm, notes: e.target.value })} placeholder="Any additional instructions..." className="bg-background border-border rounded-sm resize-none" rows={2} />
              </div>

              {!editShift && shiftForm.selectedDates.length > 0 && shiftForm.quantity > 0 && (
                <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-sm">
                  <p className="text-sm text-blue-400">This will create <strong>{shiftForm.selectedDates.length * shiftForm.quantity}</strong> shift slots ({shiftForm.selectedDates.length} days x {shiftForm.quantity} staff)</p>
                </div>
              )}

              <DialogFooter className="flex-col sm:flex-row gap-2">
                {!editShift && (
                  <Button type="button" variant="outline" onClick={addEvent} className="border-dashed border-primary text-primary hover:bg-primary/10 rounded-sm sm:mr-auto" data-testid="add-event-footer-btn">
                    <Plus className="w-4 h-4 mr-2" /> Add Another Event
                  </Button>
                )}
                <Button type="button" variant="outline" onClick={() => { setCreateOpen(false); setEditShift(null); resetForm(); }} className="border-border rounded-sm">Cancel</Button>
                <Button type="submit" data-testid="save-open-shift-btn" disabled={creating} className="bg-primary text-primary-foreground hover:bg-yellow-300 rounded-sm">
                  {creating ? 'Saving...' : (editShift ? 'Update Shift' : `Save ${eventForms.length > 1 ? `All ${eventForms.length} Events` : 'Shifts'}`)}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Manage Staff Dialog */}
        <Dialog open={!!manageShift} onOpenChange={(open) => { if (!open) { setManageShift(null); setManageGroup(null); } }}>
          <DialogContent className="bg-card border-border rounded-sm max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-barlow tracking-wider uppercase">Manage Staff</DialogTitle></DialogHeader>
            {manageShift && (
              <div className="space-y-4 mt-4">
                <div className="p-3 bg-accent/50 rounded-sm space-y-1">
                  <p className="font-medium">{manageShift.position || 'TBD'}</p>
                  <p className="text-sm text-muted-foreground">{manageShift.location || 'TBD'}</p>
                  <p className="text-sm text-muted-foreground">{formatDate(manageShift.date)} | {manageShift.start_time || 'TBD'} - {manageShift.end_time || 'TBD'}</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleSendReminder(manageShift.id)} className="flex-1 border-border rounded-sm"><Bell className="w-4 h-4 mr-2" />Reminder</Button>
                  <Button variant="outline" size="sm" onClick={() => handleBroadcast(manageShift.id)} className="flex-1 border-border rounded-sm"><Megaphone className="w-4 h-4 mr-2" />Broadcast</Button>
                </div>
                <div className="p-3 bg-accent/30 rounded-sm">
                  <p className="text-sm font-medium flex items-center gap-2"><Users className="w-4 h-4 text-primary" />Coverage: {manageShift.claimed_count || 0} / {manageShift.quantity || 1} staff assigned</p>
                  <div className="mt-2 h-2 bg-accent rounded-full overflow-hidden">
                    <div className={cn("h-full transition-all", (manageShift.claimed_count || 0) >= (manageShift.quantity || 1) ? "bg-green-500" : "bg-blue-500")} style={{ width: `${Math.min(100, ((manageShift.claimed_count || 0) / (manageShift.quantity || 1)) * 100)}%` }} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Assigned Staff</Label>
                  {manageShift.claimed_by_list?.length > 0 ? (
                    <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                      {manageShift.claimed_by_list.map((staff) => (
                        <div key={staff.user_id} className="flex items-center justify-between p-2 bg-accent rounded-sm">
                          <div className="flex items-center gap-2"><User className="w-4 h-4 text-muted-foreground" /><span>{staff.user_name}</span><Badge variant="outline" className="text-xs">{staff.user_role}</Badge></div>
                          <Button variant="ghost" size="sm" onClick={() => handleRemoveStaff(manageShift.id, staff.user_id)} className="text-destructive hover:text-destructive hover:bg-destructive/10"><X className="w-4 h-4" /></Button>
                        </div>
                      ))}
                    </div>
                  ) : <p className="text-sm text-muted-foreground p-2">No staff assigned yet</p>}
                </div>
                {(manageShift.claimed_count || 0) < (manageShift.quantity || 1) && (
                  <div className="space-y-2 pt-2 border-t border-border">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-2"><UserPlus className="w-4 h-4" />Add Staff Manually</Label>
                    <Input placeholder="Search staff..." value={staffSearchTerm} onChange={(e) => setStaffSearchTerm(e.target.value)} className="bg-background border-border rounded-sm" />
                    {staffSearchTerm && (
                      <div className="max-h-40 overflow-y-auto space-y-1">
                        {filteredUsers.filter(u => !manageShift.claimed_by_list?.some(c => c.user_id === u.id)).slice(0, 10).map(u => (
                          <button key={u.id} onClick={() => handleAddStaff(manageShift.id, u.id)} className="w-full flex items-center justify-between p-2 bg-accent hover:bg-accent rounded-sm text-left">
                            <span>{u.name}</span><Badge variant="outline" className="text-xs">{u.role}</Badge>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <DialogFooter><Button variant="outline" onClick={() => setManageShift(null)} className="border-border rounded-sm">Close</Button></DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Batch Edit Event Dialog */}
        <Dialog open={!!batchEditGroup} onOpenChange={(open) => { if (!open) { setBatchEditGroup(null); setBatchEditForm(null); setBatchEditDates([]); setBatchEditOriginalDates([]); } }}>
          <DialogContent className="bg-card border-border rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-barlow tracking-wider uppercase">Edit Event ({batchEditDates.length} day{batchEditDates.length !== 1 ? 's' : ''})</DialogTitle>
            </DialogHeader>
            {batchEditForm && (
              <form onSubmit={handleBatchUpdate} className="space-y-4 mt-4">
                <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-sm">
                  <p className="text-sm text-blue-400">Changes will apply to all <strong>{batchEditDates.length}</strong> day{batchEditDates.length !== 1 ? 's' : ''}. You can add or remove dates below.</p>
                </div>
                {/* Date Edit Calendar */}
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Edit Dates ({batchEditDates.length} selected)</Label>
                  <div className="bg-muted border border-border rounded-sm p-3">
                    <div className="flex items-center justify-between mb-3">
                      <Button type="button" variant="ghost" size="icon" onClick={() => setBatchEditMonth(m => subMonths(m, 1))} className="h-8 w-8"><ChevronLeft className="w-4 h-4" /></Button>
                      <span className="text-sm font-medium">{getMonthDays(batchEditMonth).monthLabel}</span>
                      <Button type="button" variant="ghost" size="icon" onClick={() => setBatchEditMonth(m => addMonths(m, 1))} className="h-8 w-8"><ChevronRight className="w-4 h-4" /></Button>
                    </div>
                    <div className="grid grid-cols-7 mb-1">{['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((d, i) => <div key={i} className="text-center text-xs text-muted-foreground py-1">{d}</div>)}</div>
                    <div className="grid grid-cols-7 gap-1">
                      {Array.from({ length: getMonthDays(batchEditMonth).startPadding }).map((_, i) => <div key={`pad-${i}`} className="h-8" />)}
                      {getMonthDays(batchEditMonth).days.map((day) => {
                        const isSelected = batchEditDates.includes(day.dateStr);
                        const isOriginal = batchEditOriginalDates.includes(day.dateStr);
                        const isNew = isSelected && !isOriginal;
                        return (
                          <button key={day.dateStr} type="button" onClick={() => setBatchEditDates(prev => prev.includes(day.dateStr) ? prev.filter(d => d !== day.dateStr) : [...prev, day.dateStr])}
                            className={cn("h-8 w-full text-sm rounded-sm flex items-center justify-center transition-all font-medium",
                              isNew ? "bg-green-500 text-white" : isSelected ? "bg-primary text-black" : day.isToday ? "bg-blue-500/30 text-blue-300" : "hover:bg-accent text-foreground"
                            )} data-testid={`batch-date-${day.dateStr}`}>{day.dayNum}</button>
                        );
                      })}
                    </div>
                  </div>
                  {batchEditDates.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {batchEditDates.sort().map(d => {
                        const isNew = !batchEditOriginalDates.includes(d);
                        return <Badge key={d} variant="outline" className={cn("text-xs", isNew ? "bg-green-500/20 text-green-400 border-green-500/50" : "bg-primary/20 text-primary border-primary/50")}>{format(parseISO(d), 'MMM d')}{isNew ? ' (new)' : ''}</Badge>;
                      })}
                    </div>
                  )}
                  {batchEditOriginalDates.filter(d => !batchEditDates.includes(d)).length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {batchEditOriginalDates.filter(d => !batchEditDates.includes(d)).map(d => (
                        <Badge key={d} variant="outline" className="text-xs bg-red-500/20 text-red-400 border-red-500/50 line-through">{format(parseISO(d), 'MMM d')} (removed)</Badge>
                      ))}
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-2"><Briefcase className="w-4 h-4" /> Position</Label><Input value={batchEditForm.position} onChange={(e) => setBatchEditForm({ ...batchEditForm, position: e.target.value })} className="bg-background border-border rounded-sm" data-testid="batch-edit-position" /></div>
                  <div className="space-y-2"><Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-2"><MapPin className="w-4 h-4" /> Location</Label><Input value={batchEditForm.location} onChange={(e) => setBatchEditForm({ ...batchEditForm, location: e.target.value })} className="bg-background border-border rounded-sm" data-testid="batch-edit-location" /></div>
                </div>
                <div className="space-y-2"><Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-2"><Shirt className="w-4 h-4" /> Dress Code</Label><Input value={batchEditForm.dress_code} onChange={(e) => setBatchEditForm({ ...batchEditForm, dress_code: e.target.value })} className="bg-background border-border rounded-sm" /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label className="text-xs uppercase tracking-wider text-muted-foreground">Start Time</Label><Input type="time" value={batchEditForm.start_time} onChange={(e) => setBatchEditForm({ ...batchEditForm, start_time: e.target.value })} className="bg-background border-border rounded-sm" /></div>
                  <div className="space-y-2"><Label className="text-xs uppercase tracking-wider text-muted-foreground">End Time</Label><Input type="time" value={batchEditForm.end_time} onChange={(e) => setBatchEditForm({ ...batchEditForm, end_time: e.target.value })} className="bg-background border-border rounded-sm" /></div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2"><Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-2"><Users className="w-4 h-4" /> Staff</Label><Input type="number" min="1" value={batchEditForm.quantity} onChange={(e) => setBatchEditForm({ ...batchEditForm, quantity: e.target.value })} className="bg-background border-border rounded-sm" /></div>
                  <div className="space-y-2"><Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-2"><PoundSterling className="w-4 h-4" /> Rate</Label><Input type="number" step="0.01" value={batchEditForm.hourly_payment} onChange={(e) => setBatchEditForm({ ...batchEditForm, hourly_payment: e.target.value })} className="bg-background border-border rounded-sm" /></div>
                  <div className="space-y-2"><Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-2"><Shield className="w-4 h-4" /> SIA</Label>
                    <Select value={batchEditForm.sia_required} onValueChange={(val) => setBatchEditForm({ ...batchEditForm, sia_required: val })}><SelectTrigger className="bg-background border-border rounded-sm"><SelectValue placeholder="None" /></SelectTrigger><SelectContent><SelectItem value="none">None</SelectItem><SelectItem value="door_supervisor">Door Supervisor</SelectItem><SelectItem value="cctv">CCTV</SelectItem></SelectContent></Select>
                  </div>
                </div>
                {/* Camping */}
                <div className="p-4 bg-muted border border-border rounded-sm">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm uppercase tracking-wider text-muted-foreground flex items-center gap-2"><Tent className="w-4 h-4 text-green-400" /> Camping</Label>
                    <div className="flex gap-2">
                      <Button type="button" size="sm" variant={batchEditForm.camping_provided === true ? 'default' : 'outline'} onClick={() => setBatchEditForm({ ...batchEditForm, camping_provided: true })} className={cn("rounded-sm", batchEditForm.camping_provided === true && "bg-green-600")}>Yes</Button>
                      <Button type="button" size="sm" variant={batchEditForm.camping_provided === false ? 'default' : 'outline'} onClick={() => setBatchEditForm({ ...batchEditForm, camping_provided: false })} className={cn("rounded-sm", batchEditForm.camping_provided === false && "bg-secondary")}>No</Button>
                    </div>
                  </div>
                </div>
                {/* Accommodation */}
                <div className="p-4 bg-muted border border-border rounded-sm space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm uppercase tracking-wider text-muted-foreground flex items-center gap-2"><Home className="w-4 h-4 text-blue-400" /> Accommodation</Label>
                    <div className="flex gap-2">
                      <Button type="button" size="sm" variant={batchEditForm.accommodation_provided === true ? 'default' : 'outline'} onClick={() => setBatchEditForm({ ...batchEditForm, accommodation_provided: true })} className={cn("rounded-sm", batchEditForm.accommodation_provided === true && "bg-green-600")}>Yes</Button>
                      <Button type="button" size="sm" variant={batchEditForm.accommodation_provided === false ? 'default' : 'outline'} onClick={() => setBatchEditForm({ ...batchEditForm, accommodation_provided: false })} className={cn("rounded-sm", batchEditForm.accommodation_provided === false && "bg-secondary")}>No</Button>
                    </div>
                  </div>
                  {batchEditForm.accommodation_provided === true && (
                    <div className="space-y-2 pt-2 border-t border-border"><Label className="text-xs uppercase tracking-wider text-muted-foreground">Cost Per Day</Label><Input type="number" step="0.01" value={batchEditForm.accommodation_cost_per_day} onChange={(e) => setBatchEditForm({ ...batchEditForm, accommodation_cost_per_day: e.target.value })} className="bg-background border-border rounded-sm" /></div>
                  )}
                  <div className="space-y-2 pt-2 border-t border-border"><Label className="text-xs uppercase tracking-wider text-muted-foreground">Accommodation Notes</Label><Textarea value={batchEditForm.accommodation_notes} onChange={(e) => setBatchEditForm({ ...batchEditForm, accommodation_notes: e.target.value })} placeholder="Any notes about accommodation..." className="bg-background border-border rounded-sm resize-none" rows={2} data-testid="batch-accommodation-notes" /></div>
                </div>
                {/* Transport */}
                <div className="p-4 bg-muted border border-border rounded-sm space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm uppercase tracking-wider text-muted-foreground flex items-center gap-2"><Car className="w-4 h-4 text-purple-400" /> Transport</Label>
                    <div className="flex gap-2">
                      <Button type="button" size="sm" variant={batchEditForm.transport_provided === true ? 'default' : 'outline'} onClick={() => setBatchEditForm({ ...batchEditForm, transport_provided: true })} className={cn("rounded-sm", batchEditForm.transport_provided === true && "bg-green-600")}>Yes</Button>
                      <Button type="button" size="sm" variant={batchEditForm.transport_provided === false ? 'default' : 'outline'} onClick={() => setBatchEditForm({ ...batchEditForm, transport_provided: false })} className={cn("rounded-sm", batchEditForm.transport_provided === false && "bg-secondary")}>No</Button>
                    </div>
                  </div>
                  {batchEditForm.transport_provided === true && (
                    <div className="grid grid-cols-2 gap-4 pt-2 border-t border-border">
                      <div className="space-y-2"><Label className="text-xs uppercase tracking-wider text-muted-foreground">Pickup Location</Label><Input value={batchEditForm.transport_location} onChange={(e) => setBatchEditForm({ ...batchEditForm, transport_location: e.target.value })} className="bg-background border-border rounded-sm" /></div>
                      <div className="space-y-2"><Label className="text-xs uppercase tracking-wider text-muted-foreground">Departure Time</Label><Input type="time" value={batchEditForm.transport_departure_time} onChange={(e) => setBatchEditForm({ ...batchEditForm, transport_departure_time: e.target.value })} className="bg-background border-border rounded-sm" /></div>
                    </div>
                  )}
                  <div className="space-y-2 pt-2 border-t border-border"><Label className="text-xs uppercase tracking-wider text-muted-foreground">Transport Notes</Label><Textarea value={batchEditForm.transport_notes} onChange={(e) => setBatchEditForm({ ...batchEditForm, transport_notes: e.target.value })} placeholder="Any notes about transport..." className="bg-background border-border rounded-sm resize-none" rows={2} data-testid="batch-transport-notes" /></div>
                </div>
                <div className="space-y-2"><Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-2"><FileText className="w-4 h-4" /> Notes</Label><Textarea value={batchEditForm.notes} onChange={(e) => setBatchEditForm({ ...batchEditForm, notes: e.target.value })} placeholder="Any additional instructions..." className="bg-background border-border rounded-sm resize-none" rows={2} /></div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => { setBatchEditGroup(null); setBatchEditForm(null); }} className="border-border rounded-sm">Cancel</Button>
                  <Button type="submit" disabled={creating} className="bg-primary text-primary-foreground hover:bg-yellow-300 rounded-sm" data-testid="batch-edit-save-btn">{creating ? 'Saving...' : `Update All ${batchEditDates?.length || 0} Days`}</Button>
                </DialogFooter>
              </form>
            )}
          </DialogContent>
        </Dialog>

        {/* Duplicate Dialog */}
        <Dialog open={duplicateDialog.open} onOpenChange={(open) => { if (!open) { setDuplicateDialog({ open: false, shift: null }); setDuplicateDates([]); setDuplicateMonth(new Date()); } }}>
          <DialogContent className="bg-card border-border rounded-sm max-w-md">
            <DialogHeader><DialogTitle className="font-barlow tracking-wider uppercase">Duplicate Shift</DialogTitle></DialogHeader>
            {duplicateDialog.shift && (
              <div className="space-y-4 mt-4">
                <div className="p-3 bg-accent/50 rounded-sm">
                  <p className="text-sm text-muted-foreground">Duplicating:</p>
                  <p className="font-medium">{duplicateDialog.shift.position} - {formatDate(duplicateDialog.shift.date)}</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Select New Dates ({duplicateDates.length})</Label>
                  <div className="bg-muted border border-border rounded-sm p-3">
                    <div className="flex items-center justify-between mb-3">
                      <Button type="button" variant="ghost" size="icon" onClick={() => setDuplicateMonth(m => subMonths(m, 1))} className="h-8 w-8"><ChevronLeft className="w-4 h-4" /></Button>
                      <span className="text-sm font-medium">{duplicateMonthData.monthLabel}</span>
                      <Button type="button" variant="ghost" size="icon" onClick={() => setDuplicateMonth(m => addMonths(m, 1))} className="h-8 w-8"><ChevronRight className="w-4 h-4" /></Button>
                    </div>
                    <div className="grid grid-cols-7 mb-1">{['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((d, i) => <div key={i} className="text-center text-xs text-muted-foreground py-1">{d}</div>)}</div>
                    <div className="grid grid-cols-7 gap-1">
                      {Array.from({ length: duplicateMonthData.startPadding }).map((_, i) => <div key={`pad-${i}`} className="h-8" />)}
                      {duplicateMonthData.days.map((day) => (
                        <button key={day.dateStr} type="button" onClick={() => setDuplicateDates(prev => prev.includes(day.dateStr) ? prev.filter(d => d !== day.dateStr) : [...prev, day.dateStr])}
                          className={cn("h-8 w-full text-sm rounded-sm flex items-center justify-center transition-all", duplicateDates.includes(day.dateStr) ? "bg-primary text-black font-bold" : "hover:bg-accent text-foreground")}>
                          {day.dayNum}
                        </button>
                      ))}
                    </div>
                  </div>
                  {duplicateDates.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {duplicateDates.sort().slice(0, 8).map(d => (
                        <Badge key={d} variant="outline" className="text-xs bg-primary/20 text-primary border-primary/50">{format(parseISO(d), 'MMM d')}</Badge>
                      ))}
                      {duplicateDates.length > 8 && <Badge variant="outline" className="text-xs">+{duplicateDates.length - 8} more</Badge>}
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => { setDuplicateDialog({ open: false, shift: null }); setDuplicateDates([]); }} className="border-border rounded-sm">Cancel</Button>
                  <Button onClick={handleDuplicateShift} disabled={duplicateDates.length === 0} className="bg-primary text-primary-foreground hover:bg-yellow-300 rounded-sm"><Copy className="w-4 h-4 mr-2" />Duplicate to {duplicateDates.length} dates</Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>

        <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
          <AlertDialogContent className="bg-card border-border rounded-sm">
            <AlertDialogHeader><AlertDialogTitle className="font-barlow tracking-wider uppercase">Delete Shift</AlertDialogTitle><AlertDialogDescription>Are you sure? This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
            <AlertDialogFooter><AlertDialogCancel className="border-border rounded-sm">Cancel</AlertDialogCancel><AlertDialogAction onClick={handleDeleteShift} className="bg-destructive text-destructive-foreground hover:bg-red-600 rounded-sm">Delete</AlertDialogAction></AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </SupervisorLayout>
  );
}
